import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { calculateBabyAgeInMonths } from '@/lib/utils';
import { toast } from 'sonner';

export interface Baby {
  id: string;
  user_id: string;
  name: string;
  birth_date: string;
  age_in_months: number;
  is_active: boolean;
  is_shared?: boolean;
  created_at?: string;
  updated_at?: string;
}

export function useBabies() {
  const [babies, setBabies] = useState<Baby[]>([]);
  const [removedBabies, setRemovedBabies] = useState<Baby[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { user } = useAuth();

  // Add debugging for baby fetching
  const fetchBabies = async (forceRefresh = false) => {
    if (!user) {
      setBabies([]);
      setRemovedBabies([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    
    try {
      const { data: activeBabies, error: activeError } = await supabase
        .from('babies')
        .select('*') 
        .eq('user_id', user.id)
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (activeError) {
        console.error('Error fetching active babies:', activeError);
        throw activeError;
      }

      const { data: inactiveBabies, error: inactiveError } = await supabase
        .from('babies')
        .select('*')
        .eq('user_id', user.id)
        .eq('is_active', false)
        .order('updated_at', { ascending: false });

      if (inactiveError) {
        console.error('Error fetching inactive babies:', inactiveError);
        throw inactiveError;
      }

      // Fetch shared babies via shared_access
      const { data: sharedAccessData, error: sharedError } = await supabase
        .from('shared_access')
        .select('baby_id, babies:baby_id(id, user_id, name, birth_date, age_in_months, is_active, created_at, updated_at)')
        .eq('invited_user_id', user.id)
        .not('accepted_at', 'is', null);

      if (sharedError) {
        console.error('Error fetching shared babies:', sharedError);
      }

      // Also check by email for shared access not yet linked by user_id
      const { data: sharedByEmail, error: sharedEmailError } = await supabase
        .from('shared_access')
        .select('baby_id, babies:baby_id(id, user_id, name, birth_date, age_in_months, is_active, created_at, updated_at)')
        .eq('invited_email', user.email?.toLowerCase() || '')
        .not('accepted_at', 'is', null);

      if (sharedEmailError) {
        console.error('Error fetching shared babies by email:', sharedEmailError);
      }

      // Combine and deduplicate shared babies
      const allSharedAccess = [...(sharedAccessData || []), ...(sharedByEmail || [])];
      const ownedBabyIds = new Set((activeBabies || []).map(b => b.id));
      const seenSharedIds = new Set<string>();
      const sharedBabies: Baby[] = [];
      
      for (const sa of allSharedAccess) {
        const baby = sa.babies as any;
        if (baby && baby.is_active && !ownedBabyIds.has(baby.id) && !seenSharedIds.has(baby.id)) {
          seenSharedIds.add(baby.id);
          sharedBabies.push({ ...baby, is_shared: true });
        }
      }

      const activeCount = (activeBabies?.length || 0) + sharedBabies.length;
      const inactiveCount = inactiveBabies?.length || 0;
      
      // Alder beregnes alltid fra fødselsdato og dagens dato (ikke lagret verdi)
      const withLiveAge = <T extends { birth_date: string; age_in_months: number }>(b: T): T =>
        b.birth_date ? { ...b, age_in_months: calculateBabyAgeInMonths(b.birth_date) } : b;
      setBabies([...(activeBabies || []), ...sharedBabies].map(withLiveAge) as Baby[]);
      setRemovedBabies(((inactiveBabies || []) as Baby[]).map(withLiveAge));
      
      if (forceRefresh) {
        toast.success(`Oppdatert: ${activeCount} aktive barn, ${inactiveCount} inaktive barn`);
      }
    } catch (error) {
      console.error('Error fetching babies:', error);
      toast.error('Kunne ikke hente barn');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch user's babies
  useEffect(() => {
    fetchBabies();
  }, [user]);

  // Set up real-time subscription for babies table
  useEffect(() => {
    if (!user) return;
    
    const channel = supabase
      .channel(`babies_changes-${Math.random().toString(36).slice(2)}`)
      .on('postgres_changes', 
        { 
          event: '*', 
          schema: 'public', 
          table: 'babies',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          fetchBabies(); // Refresh babies when changes occur
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  const addBaby = async (babyData: { name: string; birth_date: string }) => {
    if (!user) {
      toast.error('Du må være innlogget for å legge til barn');
      return;
    }

    try {
      const { data, error } = await supabase
        .from('babies')
        .insert({
          user_id: user.id,
          name: babyData.name.trim(),
          birth_date: babyData.birth_date,
          is_active: true
        })
        .select()
        .single();

      if (error) throw error;

      setBabies(prev => [data, ...prev]);
      toast.success(`${babyData.name} er lagt til!`);
      return data;
    } catch (error) {
      console.error('Error adding baby:', error);
      toast.error('Kunne ikke legge til barn');
      return null;
    }
  };

  const updateBaby = async (babyId: string, updates: { name?: string; birth_date?: string }) => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('babies')
        .update(updates)
        .eq('id', babyId)
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) throw error;

      setBabies(prev => prev.map(baby => baby.id === babyId ? data : baby));
      toast.success('Barn oppdatert');
      return data;
    } catch (error) {
      console.error('Error updating baby:', error);
      toast.error('Kunne ikke oppdatere barn');
      return null;
    }
  };

  const removeBaby = async (babyId: string) => {
    if (!user) return;

    try {
      const babyToRemove = babies.find(baby => baby.id === babyId);
      if (!babyToRemove) return;

      // Soft delete - mark as inactive
      const { data, error } = await supabase
        .from('babies')
        .update({ is_active: false })
        .eq('id', babyId)
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) throw error;

      setBabies(prev => prev.filter(baby => baby.id !== babyId));
      setRemovedBabies(prev => [data, ...prev]);
      toast.success(`${babyToRemove.name} er flyttet til historikk`);
      return data;
    } catch (error) {
      console.error('Error removing baby:', error);
      toast.error('Kunne ikke fjerne barn');
      return null;
    }
  };

  const restoreBaby = async (babyId: string) => {
    if (!user) return;

    try {
      const babyToRestore = removedBabies.find(baby => baby.id === babyId);
      if (!babyToRestore) return;

      const { data, error } = await supabase
        .from('babies')
        .update({ is_active: true })
        .eq('id', babyId)
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) throw error;

      setRemovedBabies(prev => prev.filter(baby => baby.id !== babyId));
      setBabies(prev => [data, ...prev]);
      toast.success(`${babyToRestore.name} er gjenopprettet`);
      return data;
    } catch (error) {
      console.error('Error restoring baby:', error);
      toast.error('Kunne ikke gjenopprette barn');
      return null;
    }
  };

  const permanentlyDeleteBaby = async (babyId: string) => {
    if (!user) return;

    try {
      const babyToDelete = removedBabies.find(baby => baby.id === babyId);
      if (!babyToDelete) return;

      const { error } = await supabase
        .from('babies')
        .delete()
        .eq('id', babyId)
        .eq('user_id', user.id);

      if (error) throw error;

      setRemovedBabies(prev => prev.filter(baby => baby.id !== babyId));
      toast.success(`${babyToDelete.name} er permanent slettet`);
    } catch (error) {
      console.error('Error permanently deleting baby:', error);
      toast.error('Kunne ikke slette barn permanent');
    }
  };

  return {
    babies,
    removedBabies,
    isLoading,
    addBaby,
    updateBaby,
    removeBaby,
    restoreBaby,
    permanentlyDeleteBaby,
    fetchBabies // Add this for manual refresh
  };
}